import { Preferences } from '@capacitor/preferences';
import { Network } from '@capacitor/network';
import { CapacitorHttp } from '@capacitor/core';

const API_SECRET = process.env.REACT_APP_API_SECRET;
const API_URL = process.env.REACT_APP_API_URL;

export type Membership = {
  id: number;
  memberid?: number;
  firstname: string;
  lastname: string;
  email: string;
  mobilenumber: string;
  verified: boolean;
  active: boolean;
  paid: boolean;
  suspended: boolean;
};

export type Registration = {
  email: string;
  password: string;
  firstname: string;
  lastname: string;
  mobilenumber: string;
};

export class AppError extends Error {
  code: string;
  message: string;
  constructor(code: string = '', message: string = '') {
    super(message);

    this.message = message;
    this.code = code;
  }
}

export const isLoggedIn = async (): Promise<boolean> => {
  try {
    const username = await Preferences.get({
      key: 'email',
    });

    if (username.value) {
      return true;
    }
  } catch (err) {
    return false;
  }

  return false;
};

export const userLogin = async (
  email: string,
  password: string,
): Promise<string> => {
  try {
    const connection = await Network.getStatus();
    if (connection.connected) {
      const response = await CapacitorHttp.request({
        method: 'POST',
        url: API_URL + '/login',
        headers: {
          Secret: API_SECRET || '',
          'Content-Type': 'application/json',
        },
        data: {
          email: email,
          password: password,
        },
      });

      if (response.data.status === 'OK') {
        return response.data.token;
      }

      throw new AppError('AUTHERR', response.data.message);
    }

    throw new AppError('CONNERR', 'No internet connection');
  } catch (err) {
    throw err;
  }
};

export const registerUser = async (
  registration: Registration,
): Promise<void> => {
  try {
    const connection = await Network.getStatus();
    if (connection.connected) {
      const response = await CapacitorHttp.request({
        url: API_URL + '/register',
        method: 'POST',
        headers: {
          Secret: API_SECRET || '',
          'Content-Type': 'application/json',
        },
        data: registration,
      });

      if (response.data.status === 'OK') {
        return;
      }

      throw new AppError('REGISTRATIONERR', response.data.message);
    }

    throw new AppError('CONNERR', 'No internet connection');
  } catch (err) {
    throw err;
  }
};

export const verifyUser = async (
  code: string,
  retry: number = 1,
): Promise<boolean> => {
  try {
    const connection = await Network.getStatus();
    if (connection.connected) {
      const token = await Preferences.get({
        key: 'token',
      });
      if (token.value) {
        const response = await CapacitorHttp.request({
          url: API_URL + '/verify',
          method: 'POST',
          headers: {
            Secret: API_SECRET || '',
            Authorization: 'BEARER ' + token.value,
            'Content-Type': 'application/json',
          },
          data: {
            code,
          },
        });

        if (response.data.status === 'OK') {
          return true;
        }

        if (response.data.error === 'token_expired') {
          if (retry >= 3) {
            throw new AppError(
              'AUTHERR',
              'could not login from saved credentials',
            );
          }
          await refreshToken();
          return verifyUser(code, retry++);
        }

        return false;
      }
    }

    throw new AppError('CONNERR', 'No internet connection');
  } catch (err) {
    throw err;
  }
};

export const resendUserVerificationCode = async (): Promise<void> => {
  const token = await Preferences.get({
    key: 'token',
  });
  if (token.value) {
    try {
      const connection = await Network.getStatus();
      if (connection.connected) {
        await CapacitorHttp.request({
          url: API_URL + '/resend',
          method: 'GET',
          headers: {
            Secret: API_SECRET || '',
            Authorization: 'BEARER ' + token.value,
            'Content-Type': 'application/json',
          },
        });
      }

      throw new AppError('CONNERR', 'No internet connection');
    } catch (err) {
      throw err;
    }
  }
};

export const logout = async (): Promise<void> => {
  try {
    await Preferences.clear();
  } catch (err) {
    throw err;
  }
};

export const getUserAccount = async (
  retry: number = 1,
): Promise<Membership> => {
  try {
    const connection = await Network.getStatus();
    if (connection.connected) {
      const token = await Preferences.get({
        key: 'token',
      });
      if (token.value) {
        const response = await CapacitorHttp.request({
          method: 'GET',
          url: API_URL + '/membership',
          headers: {
            Secret: API_SECRET || '',
            Authorization: 'BEARER ' + token.value,
            'Content-Type': 'application/json',
          },
        });

        if (response.data.status === 'OK') {
          return response.data.membership as Membership;
        }

        if (response.data.error === 'token_expired') {
          if (retry >= 3) {
            throw new AppError(
              'AUTHERR',
              'could not login from saved credentials',
            );
          }
          await refreshToken();
          return getUserAccount(retry++);
        }

        throw new AppError('AUTHERR', response.data.message);
      } else {
        throw new AppError('AUTHERR', 'could not get token from storage');
      }
    }

    throw new AppError('CONNERR', 'No internet connection');
  } catch (err) {
    throw err;
  }
};

export const saveUserCredentials = async (
  email: string,
  password: string,
  token: string,
) => {
  try {
    await Preferences.set({
      key: 'email',
      value: email,
    });

    await Preferences.set({
      key: 'password',
      value: password,
    });

    await Preferences.set({
      key: 'token',
      value: token,
    });
  } catch (err) {
    throw err;
  }
};

export const refreshToken = async (): Promise<void> => {
  try {
    const username = await Preferences.get({
      key: 'email',
    });

    const password = await Preferences.get({
      key: 'password',
    });

    if (username.value && password.value) {
      const newToken = await userLogin(username.value, password.value);
      await saveUserCredentials(username.value, password.value, newToken);
      return;
    }

    throw new AppError('AUTHERR', 'could not login from saved credentials');
  } catch (err) {
    throw err;
  }
};
